import { PopupID, PopupOpenArgs } from '../components/popups/popupRegistry';
import statePromise from 'src/lib/statePromise';
import {
  PinchType,
  SupportedOrigins,
  isIapReady,
  startGemsPinchSequence,
} from '../shop/Helpers';
import { delay, getApplication, withLoading } from 'src/lib/utils';
import { duration } from 'src/replicant/utils/duration';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { captureMessage } from '@sentry/browser';
import { promptPurchaseBooster } from '../minigame/match2-timestep/match2.sequence';
import { Match2BoosterType } from 'src/replicant/match2/match2.ruleset';
import type { CrossplayTracking } from '../crossplay/crossplay.types';
import { PurchaseGachaArgs, purchaseGacha } from '../gacha/gacha.sequence';
import { cardsOpenPopup } from '../cards/cards.sequence';
import { Feature } from 'src/replicant/analytics';
import { buddiesShareInviteDeepLinkSequence } from '../buddies/buddies.sequence';

// Only whitelisted popups can be opened.
const whiteListedPopups = [
  'popupSettings',
  'popupMatch2BundleSale',
  'popupMatch2ShopBundles',
  'popupConfirmGacha',
  'popupPetStarterPack',
  'popupBuddiesInvite',
] satisfies PopupID[];

type WhiteListedPopup = (typeof whiteListedPopups)[number];

export type DeepLink =
  | {
      type: 'popup';
      id: WhiteListedPopup;
      args?: PopupOpenArgs<WhiteListedPopup>;
    }
  | {
      type: 'showAlternativeWayToGetGems' | 'startGemsPinchSequence'; // we should always use startGemsPinchSequence, showAlternativeWayToGetGems is deprecated
      feature: SupportedOrigins;
      pinch?: PinchType;
      gemsNeeded?: number;
      crossplayTracking?: CrossplayTracking;
    }
  | {
      type: 'promptPurchaseBooster';
      boosterId: Exclude<Match2BoosterType, 'continue'>;
    }
  | {
      type: 'promptPurchaseGacha';
      gacha: PurchaseGachaArgs;
    }
  | {
      type: 'openCardCollection';
    }
  | {
      /**
       * @deprecated Fairy Forest was removed in #11760. The four Firefly
       * Garden OA messages baked this deep link into LINE chat histories
       * between 2025-06 and 2025-12, so the type stays registered and is
       * handled as a silent no-op.
       */
      type: 'fairyForest';
    }
  | {
      /**
       * @deprecated newUI was removed in #11694. Gem-shop purchases baked
       * this into native crossplay and App Store handoff payloads.
       */
      type: 'nav';
    }
  | {
      type: 'buddiesShareInvite';
      inviteMethod?: 'line' | 'native';
    }
  | {
      /**
       * @deprecated Boost Rocket was removed in #11759. Links shared during
       * the 2026-04/05 events are still live in LINE chat histories, so the
       * type stays registered and is handled as a silent no-op.
       */
      type: 'boostRocketShareInvite';
      inviteMethod?: 'line' | 'native';
    };

export async function handleDeepLink(
  opts?: DeepLink | DeepLink[],
): Promise<boolean> {
  // No deep link, bail out.
  if (!opts) return false;

  // Handle deep links array in sequence.
  if (Array.isArray(opts)) {
    const results = await Promise.all(opts.map(handleDeepLink));
    return results.some((x) => !!x);
  }

  // Invalid deep link, bail out.
  if (!opts.type) {
    return false;
  }

  // Execute action based on type.
  switch (opts.type) {
    // Open popup.
    case 'popup': {
      // Popup manager not ready yet, bail out.
      if (!getApplication().popupManager) return false;

      // Get popup data.
      const popupId = opts.id as WhiteListedPopup;
      const popupArgs = opts.args ?? ({} as PopupOpenArgs<WhiteListedPopup>);

      // Not allowed to open this popup, bail out.
      if (!whiteListedPopups.includes(popupId)) return false;

      // Wait up to a second for product catalog to be ready before showing gem shop.
      const iapReady = await Promise.race([
        statePromise(isIapReady).then(() => true),
        delay(duration({ seconds: 1 })).then(() => false),
      ]);
      if (!iapReady) return false;

      // Open popup.
      await openPopupPromise(popupId, popupArgs);

      // Break out of sequence.
      return true;
    }

    // Resume pinch sequence.
    case 'showAlternativeWayToGetGems': // Deprecated
    case 'startGemsPinchSequence': {
      // Wait up to a second for product catalog to be ready before showing gem shop.
      const iapReady = await withLoading(
        () =>
          Promise.race([
            statePromise(isIapReady).then(() => true),
            delay(duration({ seconds: 1 })).then(() => false),
          ]),
        {
          feature: Feature.IAP._,
          subFeature: Feature.IAP.READY,
        },
      );
      if (!iapReady) return false;

      await startGemsPinchSequence(
        {
          feature: opts.feature,
          pinch: opts.pinch,
          gemsNeeded: opts.gemsNeeded,
          skipAlternativeWaysGetGems: true,
          crossplayTracking: {
            feature: opts.feature,
            subFeature: `${opts.feature}_deeplink`,
            ...opts.crossplayTracking,
          },
        }, // Skip the rest of the sequence. Just open the shop.
      );
      return true;
    }

    // Prompt purchase booster.
    case 'promptPurchaseBooster': {
      await promptPurchaseBooster(opts.boosterId, false);
      return true;
    }

    // Prompt purchase gacha.
    case 'promptPurchaseGacha': {
      await purchaseGacha(opts.gacha);
      return true;
    }

    // Open card collection.
    case 'openCardCollection': {
      await cardsOpenPopup(undefined);
      return true;
    }

    // Fairy Forest was removed (#11760). Old OA message links still resolve
    // here; swallow them quietly rather than reporting them as unsupported.
    case 'fairyForest':
      return false;

    // New UI was removed (#11694). Old crossplay payloads still resolve here;
    // swallow them quietly rather than reporting them as unsupported.
    case 'nav':
      return false;

    // Buddies share invite: auto-connect sender and receiver as party buddies.
    case 'buddiesShareInvite': {
      await buddiesShareInviteDeepLinkSequence(opts);
      return true;
    }

    // Boost Rocket was removed (#11759). Old share links still resolve here;
    // swallow them quietly rather than reporting them as unsupported.
    case 'boostRocketShareInvite':
      return false;

    // Should never happen.
    default:
      reportUnsupportedDeeplinkType(opts);
      return false;
  }
}

export function createPopupDeeplink<T extends WhiteListedPopup>(
  id: T,
  args: PopupOpenArgs<T>,
): DeepLink {
  return { type: 'popup', id, args: args as PopupOpenArgs<PopupID> };
}

function reportUnsupportedDeeplinkType(type: never) {
  captureMessage('Unsupported deeplink type', {
    level: 'warning',
    extra: { type },
  });
}
